import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  useEffect(() => {
    document.title = 'About DMV-Xpress Services Las Vegas | Licensed Nevada DMV Document Prep NRS240A';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'DMV-Xpress Services is a licensed third-party Nevada DMV document preparation service in Las Vegas, NV. Licensed under NRS240A. NOT the Nevada DMV. Serving Las Vegas since 2014.');
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || ''}/about`);

    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.setAttribute('data-page-schema', 'about');
    schema.text = JSON.stringify([
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${import.meta.env.VITE_SITE_URL || ''}/about#webpage`,
        url: `${import.meta.env.VITE_SITE_URL || ''}/about`,
        name: 'About DMV-Xpress Services Las Vegas | Licensed Nevada DMV Document Prep NRS240A',
        description: 'DMV-Xpress Services is a licensed third-party Nevada DMV document preparation service in Las Vegas, NV. Licensed under NRS240A. NOT the Nevada DMV. Serving Las Vegas since 2014.',
        isPartOf: { '@type': 'WebSite', url: import.meta.env.VITE_SITE_URL || '' },
        about: {
          '@type': 'LocalBusiness',
          '@id': `${import.meta.env.VITE_SITE_URL || ''}/#business`,
          name: 'DMV-Xpress Services',
          foundingDate: '2014',
          numberOfEmployees: { '@type': 'QuantitativeValue', value: 4 },
          address: {
            '@type': 'PostalAddress',
            streetAddress: '4525 S Sandhill Rd',
            addressLocality: 'Las Vegas',
            addressRegion: 'NV',
            postalCode: '89121',
            addressCountry: 'US',
          },
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${import.meta.env.VITE_SITE_URL || ''}/#organization`,
        name: 'DMV-Xpress Services',
        legalName: 'DMV-Xpress Services™',
        url: import.meta.env.VITE_SITE_URL || '',
        foundingDate: '2014',
        foundingLocation: { '@type': 'Place', name: 'Las Vegas, Nevada' },
        description: 'Licensed third-party Nevada DMV document preparation service (NRS240A) in Las Vegas. NOT the Nevada DMV.',
        telephone: '+17028028696',
        email: 'info@dmv-xpress.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '4525 S Sandhill Rd',
          addressLocality: 'Las Vegas',
          addressRegion: 'NV',
          postalCode: '89121',
          addressCountry: 'US',
        },
        areaServed: { '@type': 'City', name: 'Las Vegas' },
        knowsAbout: ['Nevada DMV', 'Vehicle Registration', 'Title Transfer', 'License Plates', 'Notary Services', 'NRS240A'],
      },
    ]);
    document.head.appendChild(schema);
    return () => {
      const existing = document.head.querySelector('script[data-page-schema="about"]');
      if (existing) document.head.removeChild(existing);
    };
  }, []);

  const milestones = [
    { year: '2014', title: 'Founded in Las Vegas', desc: 'DMV-Xpress Services opened its doors at 4525 S Sandhill Rd, Las Vegas — built to solve the frustrating Nevada DMV wait-time problem for Clark County residents.' },
    { year: '2015', title: 'NRS240A Licensed & Bonded', desc: 'Obtained full Nevada Document Preparation Service license under NRS240A. Bonded and insured to protect every client transaction.' },
    { year: '2017', title: '1,000 Clients Served', desc: 'Reached our first major milestone — 1,000 Las Vegas drivers served. Word-of-mouth referrals became our primary growth engine.' },
    { year: '2019', title: 'Expanded Service Menu', desc: 'Added in-office notary services, duplicate title processing, and commercial fleet registration to serve the full range of Nevada DMV needs.' },
    { year: '2021', title: 'Notary Services Added', desc: 'Became a full-service notary office — allowing clients to complete title transfers, affidavits, and power of attorney documents in a single visit.' },
    { year: '2024', title: '5,000+ Clients Served', desc: 'Over 5,000 Las Vegas drivers have trusted DMV-Xpress to handle their Nevada DMV paperwork. Still growing, still appointment-only, still first-time right.' },
  ];

  const values = [
    { icon: 'ri-shield-check-fill', title: 'Licensed & Compliant', desc: 'We operate under Nevada NRS240A — the state law governing document preparation services. Every transaction is handled within the bounds of Nevada law.' },
    { icon: 'ri-file-check-fill', title: 'First-Time Right', desc: 'We know every Nevada DMV form, fee, and requirement. Our clients don\'t get rejected applications or return trips — we get it right the first time.' },
    { icon: 'ri-time-fill', title: 'Respect Your Time', desc: 'The Nevada DMV average wait is 2–4 hours. We complete most services in under 1 hour. Your time is valuable — we treat it that way.' },
    { icon: 'ri-user-heart-fill', title: 'Personal Service', desc: 'No automated systems, no web portals. You speak with a real person who knows your situation and guides you through every step.' },
    { icon: 'ri-lock-fill', title: 'Secure & Confidential', desc: 'Your personal information and vehicle documents are handled with strict confidentiality. We are bonded and insured for your protection.' },
    { icon: 'ri-map-pin-fill', title: 'Las Vegas Community', desc: 'We are a Las Vegas business serving Las Vegas residents. We understand Clark County requirements, local emissions rules, and Nevada-specific DMV processes.' },
  ];

  const credentials = [
    { icon: 'ri-government-fill', label: 'Nevada Document Preparation License', detail: 'Licensed under NRS240A — Nevada Revised Statutes governing document preparation services' },
    { icon: 'ri-building-fill', label: 'Nevada Business License', detail: 'Active Nevada state business license — Clark County registered business entity' },
    { icon: 'ri-shield-star-fill', label: 'Bonded & Insured', detail: 'Fully bonded and insured for client protection on all document preparation transactions' },
    { icon: 'ri-file-shield-fill', label: 'NOT the Nevada DMV', detail: 'We are a private third-party service — not affiliated with or part of the Nevada Department of Motor Vehicles' },
    { icon: 'ri-scales-3-fill', label: 'Not a Law Firm', detail: 'We are not attorneys and do not provide legal advice or legal representation of any kind' },
    { icon: 'ri-car-fill', label: 'On-Road Vehicles Only', detail: 'We handle on-road vehicle registration, title transfers, plates, permits, and notary — not OHV/ATV registration' },
  ];

  const team = [
    { name: 'Maria R.', role: 'Lead DMV Document Specialist', years: '9 Years', bio: 'Specializes in out-of-state vehicle transfers and commercial fleet registration. Has processed thousands of Nevada title transfers and registration applications.', img: 'https://public.readdy.ai/ai/img_res/01dcb21316bdc40779ada58b14325a90.jpg' },
    { name: 'James K.', role: 'Notary Public & Title Specialist', years: '7 Years', bio: 'Licensed Nevada Notary Public. Expert in title notarization, affidavits, power of attorney, and estate title transfers. Handles all notary services in-office.', img: 'https://readdy.ai/api/search-image?query=Professional%20confident%20African%20American%20man%20in%20his%2050s%20wearing%20dark%20navy%20business%20suit%2C%20warm%20professional%20smile%2C%20clean%20white%20studio%20background%20with%20soft%20lighting%2C%20corporate%20headshot%20photography%2C%20premium%20professional%20portrait&width=400&height=400&seq=team-james-nv-002&orientation=squarish' },
    { name: 'Sandra L.', role: 'Client Services Manager', years: '6 Years', bio: 'First point of contact for all DMV-Xpress clients. Coordinates appointments, document checklists, and ensures every client arrives prepared for a same-day service.', img: 'https://readdy.ai/api/search-image?query=Professional%20confident%20Asian%20woman%20in%20her%2030s%20wearing%20elegant%20navy%20blazer%2C%20warm%20professional%20smile%2C%20clean%20white%20studio%20background%20with%20soft%20lighting%2C%20corporate%20headshot%20photography%2C%20premium%20professional%20portrait&width=400&height=400&seq=team-sandra-nv-003&orientation=squarish' },
    { name: 'Robert M.', role: 'Commercial & Fleet Specialist', years: '5 Years', bio: 'Handles all commercial vehicle registrations, fleet accounts, and DOT compliance documentation. Expert in Nevada weight-class registration and commercial insurance requirements.', img: 'https://readdy.ai/api/search-image?query=Professional%20confident%20White%20man%20in%20his%2040s%20wearing%20dark%20navy%20business%20suit%2C%20warm%20professional%20smile%2C%20clean%20white%20studio%20background%20with%20soft%20lighting%2C%20corporate%20headshot%20photography%2C%20premium%20professional%20portrait&width=400&height=400&seq=team-robert-nv-004&orientation=squarish' },
  ];

  const limitations = [
    'Driver\'s License or State ID services — visit Nevada DMV directly',
    'Off-Highway Vehicle (OHV/ATV/UTV) registration — visit dmv.nv.gov',
    'Legal advice or legal representation of any kind',
    'Fee quotes by phone or chat — fees calculated at appointment',
    'Walk-in service — appointments required, call first',
    'Boat or watercraft registration — contact Nevada Wildlife',
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Source Sans Pro', 'Segoe UI', sans-serif" }}>
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: 420 }}>
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20Las%20Vegas%20business%20district%20daytime%20aerial%20view%20with%20modern%20office%20buildings%2C%20clean%20blue%20sky%2C%20Nevada%20desert%20landscape%20in%20background%2C%20bright%20professional%20corporate%20environment%2C%20modern%20city%20skyline%2C%20clean%20and%20professional%20business%20setting&width=1920&height=700&seq=about-hero-nv-001&orientation=landscape"
          alt="About DMV-Xpress Services — Licensed Nevada DMV Document Preparation Las Vegas"
          className="w-full h-full object-cover absolute inset-0"
          style={{ minHeight: 420 }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(0,40,85,0.92) 0%, rgba(0,40,85,0.75) 55%, rgba(0,40,85,0.4) 100%)' }}></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 pt-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-white text-xs font-bold px-4 py-2 rounded mb-5 uppercase tracking-widest" style={{ backgroundColor: '#E87722' }}>
              <i className="ri-shield-check-fill"></i>Licensed Third-Party Service — NRS240A
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
              About <span style={{ color: '#E87722' }}>DMV-Xpress</span><br />Services™
            </h1>
            <p className="text-white/80 text-lg leading-relaxed mb-6 max-w-xl">
              Las Vegas's licensed third-party Nevada DMV document preparation service since 2014. We are <strong className="text-white">NOT the Nevada DMV</strong> — we prepare and process your DMV paperwork so you don't have to wait in line.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="tel:7028028696" className="inline-flex items-center gap-2 text-white font-bold px-7 py-3 rounded text-base transition-all whitespace-nowrap" style={{ backgroundColor: '#E87722' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#d06a18'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#E87722'; }}>
                <i className="ri-phone-fill"></i>Call 702.802.8696
              </a>
              <Link to="/services/vehicle-registration" className="inline-flex items-center gap-2 bg-white/10 border border-white/40 text-white font-bold px-7 py-3 rounded text-base hover:bg-white/20 transition-all whitespace-nowrap">
                <i className="ri-list-check-2"></i>View Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── DISCLAIMER ── */}
      <div className="w-full py-3 border-b" style={{ backgroundColor: '#FFF8F0', borderColor: '#fde8cc' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-center gap-2 text-center text-sm">
          <i className="ri-information-fill flex-shrink-0" style={{ color: '#E87722' }}></i>
          <p className="text-gray-700">
            <strong style={{ color: '#003366' }}>DMV-Xpress Services is a licensed third-party document preparation service (NRS240A).</strong>
            {' '}We are NOT the Nevada Department of Motor Vehicles.{' '}
            <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="underline font-semibold" style={{ color: '#003366' }}>Visit dmv.nv.gov for official DMV services →</a>
          </p>
        </div>
      </div>

      {/* ── STATS ── */}
      <section className="py-12 border-b" style={{ backgroundColor: '#f8f9fa', borderColor: '#e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '2014', label: 'Founded in Las Vegas', icon: 'ri-building-2-fill' },
              { number: '5,000+', label: 'Clients Served', icon: 'ri-user-smile-fill' },
              { number: '10+', label: 'Years of Experience', icon: 'ri-award-fill' },
              { number: 'NRS240A', label: 'Licensed, Bonded & Insured', icon: 'ri-shield-star-fill' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#EEF4FF' }}>
                  <i className={`${stat.icon} text-xl`} style={{ color: '#003366' }}></i>
                </div>
                <p className="text-3xl font-black mb-1" style={{ color: '#003366' }}>{stat.number}</p>
                <p className="text-gray-500 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO WE ARE ── */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <h2 className="text-3xl font-black text-gray-900 mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block' }}>
                Las Vegas's Trusted DMV Document Specialists.
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed mt-5">
                <p>
                  <strong style={{ color: '#003366' }}>DMV-Xpress Services™</strong> is a licensed third-party Document Preparation Service in Las Vegas, Nevada, operating under <strong style={{ color: '#E87722' }}>Nevada Revised Statutes Chapter 240A (NRS240A)</strong>. We are NOT the Nevada Department of Motor Vehicles and are not affiliated with any government agency.
                </p>
                <p>
                  Founded in 2014, we exist to solve one of Las Vegas's most common frustrations: the Nevada DMV wait. The average Clark County DMV office wait time is 2–4 hours. We've built a service that eliminates that wait entirely.
                </p>
                <p>
                  We prepare, organize, and submit Nevada DMV paperwork on behalf of our clients. We know every form, every fee, every requirement — so your documents are correct the first time, every time. No rejected applications. No return trips. No wasted days.
                </p>
              </div>
              <div className="mt-6 p-5 rounded-lg border" style={{ backgroundColor: '#FFF8F0', borderColor: '#fde8cc' }}>
                <p className="font-bold text-sm mb-1 flex items-center gap-2" style={{ color: '#E87722' }}>
                  <i className="ri-information-fill"></i>Important Disclaimer
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  DMV-Xpress Services is a private document preparation service. We are not attorneys and do not provide legal advice. We are not the Nevada DMV. All Nevada DMV state fees are separate from our service fees. For Driver's License, State ID, and OHV registration, visit <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="font-semibold underline" style={{ color: '#003366' }}>dmv.nv.gov</a>.
                </p>
              </div>
            </div>
            <div className="relative rounded-lg overflow-hidden border" style={{ borderColor: '#e5e7eb', height: 480 }}>
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20DMV%20document%20preparation%20office%20interior%20in%20Las%20Vegas%2C%20clean%20bright%20reception%20desk%20with%20professional%20staff%20member%20reviewing%20vehicle%20title%20documents%2C%20modern%20professional%20office%20decor%2C%20warm%20professional%20lighting%2C%20high-end%20automotive%20document%20service%20center%2C%20clean%20business%20environment%20with%20navy%20and%20white%20color%20scheme&width=900&height=700&seq=about-office-nv-002&orientation=portrait"
                alt="DMV-Xpress Services Las Vegas Office — Licensed Third-Party Nevada DMV Document Preparation"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 p-5" style={{ background: 'linear-gradient(to top, rgba(0,40,85,0.95) 0%, transparent 100%)' }}>
                <div className="grid grid-cols-3 gap-3">
                  {[{ n: '≤ 1 Hr', l: 'Avg Service Time' }, { n: '100%', l: 'Appointment-Based' }, { n: 'NRS240A', l: 'Licensed' }].map((s, i) => (
                    <div key={i} className="rounded-lg p-3 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}>
                      <p className="font-black text-lg leading-none" style={{ color: '#E87722' }}>{s.n}</p>
                      <p className="text-white/70 text-xs mt-1">{s.l}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── OUR VALUES ── */}
      <section className="py-16 border-t" style={{ borderColor: '#e5e7eb', backgroundColor: '#f8f9fa' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block' }}>
            Our Core Values.
          </h2>
          <p className="text-gray-600 mt-4 mb-10 text-base max-w-2xl">
            Every decision we make is guided by these principles — from how we answer the phone to how we prepare your Nevada DMV paperwork.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {values.map((v, i) => (
              <div key={i} className="bg-white p-6 rounded-lg border hover:border-orange-300 transition-all" style={{ borderColor: '#e5e7eb' }}>
                <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: '#EEF4FF' }}>
                  <i className={`${v.icon} text-xl`} style={{ color: '#003366' }}></i>
                </div>
                <h3 className="font-bold text-gray-900 text-base mb-2">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CREDENTIALS ── */}
      <section className="py-16 border-t" style={{ borderColor: '#e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            <div>
              <h2 className="text-3xl font-black text-gray-900 mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block' }}>
                Licensed, Bonded &amp; Compliant.
              </h2>
              <p className="text-gray-600 mt-4 mb-8 leading-relaxed">
                DMV-Xpress Services operates under Nevada law as a licensed Document Preparation Service. Here's exactly what that means — and what it doesn't mean.
              </p>
              <div className="space-y-3">
                {credentials.map((c, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 rounded-lg border hover:border-orange-300 transition-colors" style={{ borderColor: '#e5e7eb' }}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#EEF4FF' }}>
                      <i className={`${c.icon} text-lg`} style={{ color: '#003366' }}></i>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm mb-0.5">{c.label}</p>
                      <p className="text-gray-500 text-xs leading-relaxed">{c.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-black text-gray-900 mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block' }}>
                What We Don't Do.
              </h2>
              <p className="text-gray-600 mt-4 mb-8 leading-relaxed">
                Transparency is important to us. Here's what DMV-Xpress does NOT offer — and where to go instead.
              </p>
              <div className="space-y-3 mb-8">
                {limitations.map((l, i) => (
                  <div key={i} className="flex items-center gap-3 p-4 rounded-lg border" style={{ borderColor: '#fee2e2', backgroundColor: '#fff5f5' }}>
                    <i className="ri-close-circle-fill flex-shrink-0" style={{ color: '#ef4444' }}></i>
                    <span className="text-gray-600 text-sm">{l}</span>
                  </div>
                ))}
              </div>
              <div className="p-5 rounded-lg border" style={{ backgroundColor: '#FFF8F0', borderColor: '#fde8cc' }}>
                <p className="font-bold text-sm mb-2 flex items-center gap-2" style={{ color: '#E87722' }}>
                  <i className="ri-external-link-fill"></i>For Services We Don't Offer:
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Visit the official Nevada DMV at <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="font-semibold underline" style={{ color: '#003366' }}>dmv.nv.gov</a> or call the Nevada DMV at <strong className="text-gray-800">(775) 684-4368</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TIMELINE ── */}
      <section className="py-16 border-t" style={{ borderColor: '#e5e7eb', backgroundColor: '#f8f9fa' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block' }}>
            10+ Years Serving Las Vegas.
          </h2>
          <p className="text-gray-600 mt-4 mb-10 text-base max-w-2xl">
            From a single-service office to Las Vegas's most trusted Nevada DMV document preparation service.
          </p>
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 hidden md:block" style={{ backgroundColor: '#e5e7eb' }}></div>
            <div className="space-y-6">
              {milestones.map((m, i) => (
                <div key={i} className="relative flex gap-8 items-start">
                  <div className="hidden md:flex w-16 h-16 rounded-xl items-center justify-center flex-shrink-0 z-10 text-white font-black text-sm" style={{ backgroundColor: '#003366' }}>
                    {m.year}
                  </div>
                  <div className="flex-1 bg-white p-6 rounded-lg border hover:border-orange-300 transition-all" style={{ borderColor: '#e5e7eb' }}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="md:hidden font-bold text-sm px-3 py-1 rounded-full text-white" style={{ backgroundColor: '#003366' }}>{m.year}</span>
                      <h3 className="font-bold text-gray-900 text-base">{m.title}</h3>
                    </div>
                    <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── TEAM ── */}
      <section className="py-16 border-t" style={{ borderColor: '#e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block' }}>
            Meet Our Team.
          </h2>
          <p className="text-gray-600 mt-4 mb-10 text-base max-w-2xl">
            Experienced Nevada DMV document specialists who know every form, every fee, and every requirement.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {team.map((member, i) => (
              <div key={i} className="rounded-lg border overflow-hidden hover:border-orange-300 transition-all group" style={{ borderColor: '#e5e7eb' }}>
                <div className="relative h-52 overflow-hidden">
                  <img src={member.img} alt={`${member.name} — ${member.role} at DMV-Xpress Services Las Vegas`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute bottom-3 left-3">
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: '#E87722' }}>{member.years}</span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-gray-900 text-base mb-0.5">{member.name}</h3>
                  <p className="text-sm font-semibold mb-3" style={{ color: '#E87722' }}>{member.role}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 relative overflow-hidden" style={{ backgroundColor: '#003366' }}>
        <div className="absolute top-0 left-0 right-0 h-8 overflow-hidden">
          <svg viewBox="0 0 1440 32" preserveAspectRatio="none" className="w-full h-full" style={{ fill: '#f8f9fa' }}>
            <path d="M0,0 C360,32 1080,32 1440,0 L1440,0 L0,0 Z"></path>
          </svg>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center pt-8">
          <h2 className="text-3xl font-black text-white mb-4 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block' }}>
            Ready to Skip the Nevada DMV Line?
          </h2>
          <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto mt-4">
            Call us to schedule your appointment. We'll tell you exactly what to bring and have you in and out in under an hour.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:7028028696" className="inline-flex items-center justify-center gap-3 text-white px-10 py-4 rounded font-bold transition-all text-base whitespace-nowrap" style={{ backgroundColor: '#E87722' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#d06a18'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#E87722'; }}>
              <i className="ri-phone-fill text-xl"></i>Call 702.802.8696
            </a>
            <Link to="/contact" className="inline-flex items-center justify-center gap-3 border-2 border-white/40 text-white px-10 py-4 rounded font-bold hover:bg-white hover:text-gray-900 transition-all text-base whitespace-nowrap">
              <i className="ri-mail-send-line text-xl"></i>Send a Message
            </Link>
          </div>
          <p className="text-white/40 text-sm mt-6">By appointment only — Mon–Fri 9AM–5PM, Sat 10AM–3PM</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
